import type { RoundListItem } from "@dishour/shared";
import { useState } from "react";
import { useCommit, useConfirmCommitment, useRounds, useWithdraw } from "../../api/rounds.js";
import { AppShell } from "../../components/AppShell.js";
import { EmptyState } from "../../components/EmptyState.js";
import { ProgressBar } from "../../components/ProgressBar.js";
import { Skeleton } from "../../components/Skeleton.js";
import { CommitmentStatusBadge, RoundStatusBadge } from "../../components/StatusBadge.js";
import { QtyInput } from "../../components/QtyInput.js";
import { formatMoney, formatQty, formatRelativeDeadline } from "../../lib/format.js";

function savingsPercent(regular: string, deal: string): number {
  return Math.round((1 - Number(deal) / Number(regular)) * 100);
}

function RoundCard({ round }: { round: RoundListItem }) {
  const [qty, setQty] = useState(round.myCommitment?.qty ?? round.sku.qtyStep);
  const commit = useCommit(round.id);
  const confirm = useConfirmCommitment(round.id);
  const withdraw = useWithdraw(round.id);
  const busy = commit.isPending || confirm.isPending || withdraw.isPending;
  const error = commit.error ?? confirm.error ?? withdraw.error;

  return (
    <li className="mb-4 rounded-xl border border-stone-200 bg-white p-4">
      <div className="mb-2 flex items-start justify-between gap-2">
        <div>
          <h2 className="text-lg font-semibold">{round.sku.nameKo}</h2>
          <p className="text-sm text-stone-500">
            {round.sku.supplier.name}
            {round.sku.packSize ? ` · ${round.sku.packSize}` : ""}
          </p>
        </div>
        <RoundStatusBadge status={round.status} />
      </div>

      <p className="mb-3">
        <span className="text-2xl font-bold">{formatMoney(round.dealUnitPrice)}</span>
        <span className="ml-2 text-stone-400 line-through">{formatMoney(round.regularUnitPrice)}</span>
        <span className="ml-2 font-semibold text-emerald-700">
          {savingsPercent(round.regularUnitPrice, round.dealUnitPrice)}% 절감
        </span>
      </p>

      <ProgressBar percent={round.progressPercent} met={round.thresholdMet} />
      <p className="mt-1 mb-3 text-sm text-stone-600">
        {formatQty(round.committedQty, round.sku)} / 목표 {formatQty(round.thresholdQty, round.sku)} ·{" "}
        {formatRelativeDeadline(round.closesAt)}
      </p>

      {round.myCommitment ? (
        <div className="mb-3 flex items-center gap-2 text-sm">
          <CommitmentStatusBadge status={round.myCommitment.status} />
          <span>{formatQty(round.myCommitment.qty, round.sku)} 신청</span>
        </div>
      ) : null}

      {round.status === "open" ? (
        <div className="flex flex-col gap-3">
          <QtyInput value={qty} sku={round.sku} onChange={setQty} disabled={busy} />
          <button
            type="button"
            className="rounded-lg bg-brand-700 px-4 py-3 font-semibold text-white hover:bg-brand-800 disabled:opacity-50"
            disabled={busy}
            onClick={() => commit.mutate(qty)}
          >
            {round.myCommitment ? "수량 변경" : "참여하기"}
          </button>
          {round.myCommitment ? (
            <button
              type="button"
              className="text-sm text-stone-500 underline"
              disabled={busy}
              onClick={() => withdraw.mutate()}
            >
              참여 취소
            </button>
          ) : null}
        </div>
      ) : null}

      {round.status === "confirming" && round.myCommitment?.status === "soft" ? (
        <button
          type="button"
          className="w-full rounded-lg bg-amber-600 px-4 py-3 font-semibold text-white disabled:opacity-50"
          disabled={busy}
          onClick={() => confirm.mutate()}
        >
          최종 확인하기 ({formatQty(round.myCommitment.qty, round.sku)})
        </button>
      ) : null}

      {error ? (
        <p role="alert" className="mt-2 text-sm text-red-600">
          {(error as Error).message}
        </p>
      ) : null}
    </li>
  );
}

export function RestaurantRoundsPage() {
  const { data, isPending, error } = useRounds();

  return (
    <AppShell title="이번 주 공동구매">
      {isPending ? <Skeleton /> : null}
      {error ? (
        <p role="alert" className="text-red-600">
          목록을 불러오지 못했습니다.
        </p>
      ) : null}
      {data && data.length === 0 ? <EmptyState message="진행 중인 공동구매가 없습니다." /> : null}
      <ul>{data?.map((round) => <RoundCard key={round.id} round={round} />)}</ul>
    </AppShell>
  );
}
